import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { getSpellDefinition } from '@shared/data/spells';
import { getItemGrantingSpell } from '@shared/data/items';
import { getSummonDisplayForSpell } from '@shared/utils/summonDisplay';
import { createCloseButton, getIconTexture } from '../../sprites/IconAtlases';
import { getSpellInfoRows } from './spellInfoRows';

const PLACEHOLDER_ICON = 'item_icon_noicon';
const THUMB_SIZE = 48;
const DETAIL_ICON_SIZE = 48;

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

/** Returns the texture key for a spell icon, falling back to placeholder */
function resolveSpellIcon(scene: Phaser.Scene, spellId: string): string {
  const spell = getSpellDefinition(spellId);
  if (spell?.icon && scene.textures.exists(spell.icon)) return spell.icon;
  return PLACEHOLDER_ICON;
}

type SpellsPanelCallbacks = {
  onMove(x: number, y: number): void;
  onAssignToQuickBar(spellId: string): void;
  onAssignToSlot(spellId: string, slotIndex: number): void;
  /** Tamaño (px) del slot de la hotbar, para dimensionar el preview del drag. */
  getBarSlotSize(): number;
  /** Se soltó un hechizo arrastrado en (pointer); GameScene resuelve el slot. */
  onDropSpellAtPointer(spellId: string, pointer: Phaser.Input.Pointer): void;
};

type SpellsPanelOptions = { initialX: number; initialY: number };

interface ListRowUI {
  hit: Phaser.GameObjects.Rectangle;
  icon: Phaser.GameObjects.Image;
  text: Phaser.GameObjects.Text;
}

/** Ms que hay que mantener apretada una fila para que arranque el drag. */
const DRAG_HOLD_MS = 150;

export class SpellsPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: SpellsPanelCallbacks;
  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  // Drag de un hechizo del listado hacia la hotbar (hold ≥ DRAG_HOLD_MS).
  private spellDrag: {
    spellId: string;
    active: boolean;
    preview: Phaser.GameObjects.Image | null;
    holdTimer: Phaser.Time.TimerEvent | null;
  } | null = null;

  private learnedSpellIds: string[] = [];
  private selectedSpellId: string | null = null;
  private scrollOffset = 0;

  // UI
  private bg: Phaser.GameObjects.Rectangle | null = null;
  private title: Phaser.GameObjects.Text | null = null;
  private closeBtn: Phaser.GameObjects.Sprite | null = null;
  private listBg: Phaser.GameObjects.Rectangle | null = null;
  private listRows: ListRowUI[] = [];
  private scrollUp: Phaser.GameObjects.Text | null = null;
  private scrollDown: Phaser.GameObjects.Text | null = null;
  private detailIcon: Phaser.GameObjects.Image | null = null;
  private detailName: Phaser.GameObjects.Text | null = null;
  private detailDesc: Phaser.GameObjects.Text | null = null;
  /** Ficha iconográfica de características (una fila icono+texto por atributo). */
  private detailSpellInfo: Phaser.GameObjects.Container | null = null;
  private detailSpellInfoY = 0;
  private detailSummon: Phaser.GameObjects.Text | null = null;
  private detailGrant: Phaser.GameObjects.Text | null = null;
  private assignBtn: Phaser.GameObjects.Text | null = null;
  private instructionText: Phaser.GameObjects.Text | null = null;
  private noSpellsText: Phaser.GameObjects.Text | null = null;

  private panelX = 0;
  private panelY = 0;
  private readonly pw = 480;
  // Panel más alto y filas más altas para que entren los íconos de lista de 48px (= tamaño
  // grande, pedido de Dalmiro). 6 filas visibles + scroll.
  private readonly ph = 400;
  private readonly visibleRows = 6;
  private readonly rowH = 52;

  constructor(scene: Phaser.Scene, callbacks: SpellsPanelCallbacks, options: SpellsPanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.panelX = options.initialX;
    this.panelY = options.initialY;
    this.buildPanel();
    this.setVisible(false);
  }

  // === PUBLIC ================================================================
  toggle(): void { this.isOpen = !this.isOpen; this.setVisible(this.isOpen); if (this.isOpen) this.refreshList(); }
  open(): void { this.isOpen = true; this.setVisible(true); this.refreshList(); }
  close(): void { this.isOpen = false; this.setVisible(false); }
  isOpenPanel(): boolean { return this.isOpen; }

  setLearnedSpells(ids: string[]): void { this.learnedSpellIds = ids; if (this.isOpen) this.refreshList(); }
  addLearnedSpell(id: string): void { if (!this.learnedSpellIds.includes(id)) { this.learnedSpellIds.push(id); if (this.isOpen) this.refreshList(); } }

  handlePointerMove(p: Phaser.Input.Pointer): void {
    // Drag de un hechizo hacia la hotbar: mover el preview.
    if (this.spellDrag?.active && this.spellDrag.preview) {
      this.spellDrag.preview.setPosition(p.x, p.y);
    }
    // Drag de la ventana por el título.
    if (this.isDragging) {
      this.movePanel(p.x - this.dragOffsetX, p.y - this.dragOffsetY);
      this.callbacks.onMove(this.panelX, this.panelY);
    }
  }
  handlePointerUp(p?: Phaser.Input.Pointer): void {
    this.isDragging = false;
    // Soltar un hechizo arrastrado: GameScene resuelve el slot bajo el cursor.
    if (this.spellDrag?.active && p) {
      this.callbacks.onDropSpellAtPointer(this.spellDrag.spellId, p);
    }
    this.cancelSpellDrag();
  }
  isDraggingPanel(): boolean { return this.isDragging; }
  isDraggingSpell(): boolean { return !!this.spellDrag?.active; }
  /** Hay un drag de hechizo activo O un hold pendiente (para rutear el pointerup global). */
  hasSpellDrag(): boolean { return !!this.spellDrag; }

  /** Activa el drag (llamado por el timer de hold): crea el preview grande. */
  private activateSpellDrag(p: Phaser.Input.Pointer): void {
    if (!this.spellDrag) return;
    this.spellDrag.holdTimer = null;
    const size = this.callbacks.getBarSlotSize();
    const preview = this.scene.add
      .image(p.x, p.y, resolveSpellIcon(this.scene, this.spellDrag.spellId))
      .setOrigin(0.5)
      .setDisplaySize(size, size)
      .setDepth(1250)
      .setScrollFactor(0)
      .setAlpha(0.9);
    this.spellDrag.preview = preview;
    this.spellDrag.active = true;
  }

  /** Cancela cualquier drag/hold en curso y limpia el preview. */
  private cancelSpellDrag(): void {
    if (!this.spellDrag) return;
    this.spellDrag.holdTimer?.remove(false);
    this.spellDrag.preview?.destroy();
    this.spellDrag = null;
  }
  isPointerOver(p: Phaser.Input.Pointer): boolean {
    if (!this.isOpen) return false;
    const l = this.panelX - this.pw / 2, r = this.panelX + this.pw / 2;
    const t = this.panelY - this.ph / 2, b = this.panelY + this.ph / 2;
    return p.x >= l && p.x <= r && p.y >= t && p.y <= b;
  }

  // === BUILD =================================================================
  private buildPanel(): void {
    const { panelX: px, panelY: py, pw, ph } = this;
    const bx = px - pw / 2, by = py - ph / 2;
    const pad = 10;
    const listW = pw * 0.46;
    const detW = pw - listW - pad * 3;
    const lx = bx + pad;              // left col x
    const rx = lx + listW + pad;      // right col x

    this.bg = this.scene.add.rectangle(px, py, pw, ph, 0x111111, 0.92)
      .setOrigin(0.5).setStrokeStyle(1, 0x666666).setDepth(1100).setScrollFactor(0);

    this.title = this.scene.add.text(px, by + 16, 'Hechizos (H)', withGameFont({ fontSize: '24px', color: '#ffffff' }))
      .setOrigin(0.5, 0.5).setDepth(1101).setScrollFactor(0).setInteractive({ useHandCursor: true });
    this.title.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.isDragging = true; this.dragOffsetX = p.x - this.panelX; this.dragOffsetY = p.y - this.panelY; p.event.stopPropagation();
    });

    this.closeBtn = createCloseButton(this.scene, px + pw / 2 - 14, by + 14, 14)!
      .setOrigin(1, 0).setDepth(1101);
    this.closeBtn.on('pointerup', (p: Phaser.Input.Pointer) => { this.close(); p.event.stopPropagation(); });

    this.listBg = this.scene.add.rectangle(lx + listW / 2, py + 8, listW, ph - pad * 2 - 24, 0x1a1a1a, 0.8)
      .setOrigin(0.5).setStrokeStyle(1, 0x444444).setDepth(1100).setScrollFactor(0);

    this.scrollUp = this.scene.add.text(lx + listW - 6, by + 30, '▲', withGameFont({ fontSize: '18px', color: '#ffffaa' }))
      .setOrigin(1, 0).setDepth(1101).setScrollFactor(0).setInteractive({ useHandCursor: true });
    this.scrollUp.on('pointerup', (p: Phaser.Input.Pointer) => { this.scrollOffset = Math.max(0, this.scrollOffset - this.visibleRows); this.refreshList(); p.event.stopPropagation(); });

    this.scrollDown = this.scene.add.text(lx + listW - 6, by + ph - 22, '▼', withGameFont({ fontSize: '18px', color: '#ffffaa' }))
      .setOrigin(1, 0).setDepth(1101).setScrollFactor(0).setInteractive({ useHandCursor: true });
    this.scrollDown.on('pointerup', (p: Phaser.Input.Pointer) => { const m = Math.max(0, this.learnedSpellIds.length - this.visibleRows); this.scrollOffset = Math.min(m, this.scrollOffset + this.visibleRows); this.refreshList(); p.event.stopPropagation(); });

    // List rows with thumbnail icon + name. Un hit-rect transparente cubre toda
    // la fila (target de agarre completo) y maneja click / hold-drag.
    const listStartY = by + 42;
    for (let i = 0; i < this.visibleRows; i++) {
      const y = listStartY + i * this.rowH;
      const rowCY = y + this.rowH / 2;

      const hit = this.scene.add.rectangle(lx + listW / 2, rowCY, listW, this.rowH, 0x000000, 0.001)
        .setOrigin(0.5).setDepth(1101).setScrollFactor(0).setInteractive({ useHandCursor: true });

      const icon = this.scene.add.image(lx + 8 + THUMB_SIZE / 2, rowCY, PLACEHOLDER_ICON)
        .setDisplaySize(THUMB_SIZE, THUMB_SIZE).setOrigin(0.5).setDepth(1101).setScrollFactor(0);

      // Texto centrado verticalmente con el ícono, separado del ícono (antes iba pegado).
      const txt = this.scene.add.text(lx + 8 + THUMB_SIZE + 14, rowCY, '', withGameFont({ fontSize: '20px', color: '#ffffff' }))
        .setOrigin(0, 0.5).setDepth(1101).setScrollFactor(0);

      const idx = i;
      let lastClick = 0;

      hit.on('pointerdown', (p: Phaser.Input.Pointer) => {
        const sid = this.learnedSpellIds[this.scrollOffset + idx];
        if (!sid) return;
        // Arrancar el hold; si se mantiene DRAG_HOLD_MS apretado → drag.
        this.cancelSpellDrag();
        const holdTimer = this.scene.time.delayedCall(DRAG_HOLD_MS, () => this.activateSpellDrag(p));
        this.spellDrag = { spellId: sid, active: false, preview: null, holdTimer };
        p.event.stopPropagation();
      });

      hit.on('pointerup', (p: Phaser.Input.Pointer) => {
        // Si el drag ya se activó, lo resuelve el pointerup global (handlePointerUp).
        if (this.spellDrag?.active) return;
        // Release corto (< hold) → click normal: select / doble-click asigna.
        this.cancelSpellDrag();
        const now = Date.now();
        if (now - lastClick < 350) {
          const sid = this.learnedSpellIds[this.scrollOffset + idx];
          if (sid) this.callbacks.onAssignToQuickBar(sid);
        } else {
          this.handleRowClick(idx);
        }
        lastClick = now;
        p.event.stopPropagation();
      });

      this.listRows.push({ hit, icon, text: txt });
    }

    // Detail section — icon large + info
    const detY = by + 38;

    this.detailIcon = this.scene.add.image(rx + DETAIL_ICON_SIZE / 2, detY + DETAIL_ICON_SIZE / 2, PLACEHOLDER_ICON)
      .setDisplaySize(DETAIL_ICON_SIZE, DETAIL_ICON_SIZE).setOrigin(0.5).setDepth(1101).setScrollFactor(0).setVisible(false);

    const infoX = rx + DETAIL_ICON_SIZE + 8;

    this.detailName = this.scene.add.text(infoX, detY, '', withGameFont({ fontSize: '22px', color: '#ffff88', fontStyle: 'bold' }))
      .setOrigin(0, 0).setDepth(1101).setScrollFactor(0);

    this.detailDesc = this.scene.add.text(rx, detY + DETAIL_ICON_SIZE + 8, '', withGameFont({ fontSize: '16px', color: '#cccccc', wordWrap: { width: detW } }))
      .setOrigin(0, 0).setDepth(1101).setScrollFactor(0);

    // Ficha iconográfica de características (icono + texto por atributo), reconstruida en
    // updateDetail vía getSpellInfoRows (misma fuente que el vendedor de hechizos).
    this.detailSpellInfoY = detY + DETAIL_ICON_SIZE + 44;
    this.detailSpellInfo = this.scene.add.container(rx, this.detailSpellInfoY, [])
      .setDepth(1101).setScrollFactor(0);

    // Ficha de la criatura invocada (sólo hechizos de invocación). Y dinámico bajo la ficha
    // de características (updateDetail lo reposiciona). Ver docs/SUMMONS.md.
    this.detailSummon = this.scene.add.text(rx, detY + DETAIL_ICON_SIZE + 80, '', withGameFont({ fontSize: '16px', color: '#c8b070', wordWrap: { width: detW } }))
      .setOrigin(0, 0).setDepth(1101).setScrollFactor(0);

    // Hechizo temporal otorgado por un item (grantOnly + grantsSpellId). Aguamarina para
    // distinguirlo. Se desaprende al desequipar el item. Ver docs/SPELLS.md.
    this.detailGrant = this.scene.add.text(rx, detY + DETAIL_ICON_SIZE + 112, '', withGameFont({ fontSize: '16px', color: '#7fffd4', wordWrap: { width: detW } }))
      .setOrigin(0, 0).setDepth(1101).setScrollFactor(0);

    this.noSpellsText = this.scene.add.text(px, py + 10, 'No has aprendido ningún hechizo.\nUsa un pergamino para aprender.', withGameFont({ fontSize: '18px', color: '#666666', align: 'center' }))
      .setOrigin(0.5, 0.5).setDepth(1101).setScrollFactor(0);

    this.instructionText = this.scene.add.text(lx + listW / 2, by + ph - 10, 'Arrastrá un hechizo a la barra', withGameFont({ fontSize: '14px', color: '#666666', align: 'center' }))
      .setOrigin(0.5, 1).setDepth(1101).setScrollFactor(0);

    this.assignBtn = this.scene.add.text(rx + detW / 2, by + ph - 32, '[ Asignar a barra ]', withGameFont({ fontSize: '20px', color: '#88aaff', backgroundColor: '#333333' }))
      .setOrigin(0.5, 0.5).setPadding(8, 4, 8, 4).setDepth(1101).setScrollFactor(0).setInteractive({ useHandCursor: true });
    this.assignBtn.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (this.selectedSpellId) this.showSlotPickerOverlay(this.selectedSpellId);
      p.event.stopPropagation();
    });
  }

  // === SLOT PICKER OVERLAY ===================================================

  private slotPickerOverlay: Phaser.GameObjects.Rectangle | null = null;
  private slotPickerText: Phaser.GameObjects.Text | null = null;
  private slotPickerKeyHandler: ((event: KeyboardEvent) => void) | null = null;

  /** True while the slot picker overlay is visible */
  public isPickingSlot = false;

  private showSlotPickerOverlay(spellId: string): void {
    this.hideSlotPickerOverlay();
    this.isPickingSlot = true;

    const { width, height } = this.scene.scale;

    // Dark overlay covering entire screen
    this.slotPickerOverlay = this.scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.7)
      .setScrollFactor(0).setDepth(1200).setInteractive(); // block clicks below

    // Instruction text
    const spell = getSpellDefinition(spellId);
    const spellName = spell?.nameEs ?? spellId;
    this.slotPickerText = this.scene.add.text(width / 2, height / 2 - 30,
      `Asignar "${spellName}" a la barra rápida\n\nPresioná la tecla del slot (1-9, 0)`,
      withGameFont({ fontSize: '14px', color: '#ffffff', align: 'center' })
    ).setOrigin(0.5).setScrollFactor(0).setDepth(1201);

    // Listen for keypress
    this.slotPickerKeyHandler = (event: KeyboardEvent) => {
      // Prevent Phaser and other listeners from processing this key
      event.preventDefault();
      event.stopImmediatePropagation();

      // Teclas 1..9 → slots 0..8; 0 → slot 10 (índice 9). Igual layout que la barra.
      const key = event.key;
      let slotIndex = -1;
      if (key >= '1' && key <= '9') slotIndex = Number(key) - 1;
      else if (key === '0') slotIndex = 9;

      if (slotIndex >= 0) {
        this.callbacks.onAssignToSlot(spellId, slotIndex);
      }
      // Any key closes the overlay
      this.hideSlotPickerOverlay();
    };

    // Use capture phase to intercept before Phaser
    window.addEventListener('keydown', this.slotPickerKeyHandler, { once: true, capture: true });
  }

  private hideSlotPickerOverlay(): void {
    this.isPickingSlot = false;
    if (this.slotPickerOverlay) { this.slotPickerOverlay.destroy(); this.slotPickerOverlay = null; }
    if (this.slotPickerText) { this.slotPickerText.destroy(); this.slotPickerText = null; }
    if (this.slotPickerKeyHandler) {
      window.removeEventListener('keydown', this.slotPickerKeyHandler, { capture: true } as EventListenerOptions);
      this.slotPickerKeyHandler = null;
    }
  }

  // === LIST LOGIC ============================================================
  private handleRowClick(vi: number): void {
    const sid = this.learnedSpellIds[this.scrollOffset + vi];
    if (!sid) return;
    this.selectedSpellId = sid;
    this.refreshList();
  }

  private refreshList(): void {
    const has = this.learnedSpellIds.length > 0;
    this.noSpellsText?.setVisible(!has);
    this.assignBtn?.setVisible(has && !!this.selectedSpellId);

    for (let i = 0; i < this.listRows.length; i++) {
      const sid = this.learnedSpellIds[this.scrollOffset + i];
      const row = this.listRows[i];
      if (sid) {
        const spell = getSpellDefinition(sid);
        // Hechizos temporales (grantOnly, otorgados por un item equipado) se marcan con
        // sufijo "(temporal)" y color aguamarina para distinguirlos de los permanentes.
        const temp = !!spell?.grantOnly;
        row.text.setText((spell?.nameEs ?? sid) + (temp ? '  (temporal)' : ''));
        row.text.setColor(sid === this.selectedSpellId ? '#ffff00' : temp ? '#7fffd4' : '#ffffff');
        row.icon.setTexture(resolveSpellIcon(this.scene, sid)).setDisplaySize(THUMB_SIZE, THUMB_SIZE).setVisible(true);
      } else {
        row.text.setText('');
        row.icon.setVisible(false);
      }
    }
    this.updateDetail();
  }

  /**
   * Reconstruye la ficha iconográfica (icono + texto por característica) desde
   * getSpellInfoRows. Devuelve el alto total ocupado en px (para apilar debajo el
   * bloque de invocación / hechizo temporal). Misma fuente que el vendedor de hechizos.
   */
  private rebuildSpellInfoRows(spell: ReturnType<typeof getSpellDefinition>): number {
    const c = this.detailSpellInfo;
    if (!c || !spell) return 0;
    c.removeAll(true);
    const ROW_H = 14;
    const ICON = 12;
    const GAP = 4;
    const rows = getSpellInfoRows(spell);
    rows.forEach((row, i) => {
      const y = i * ROW_H;
      const tex = getIconTexture(row.icon);
      let textX = 0;
      if (tex && this.scene.textures.exists(tex.textureKey)) {
        const sprite = this.scene.add.sprite(0, y + ICON / 2, tex.textureKey, tex.frame)
          .setOrigin(0, 0.5).setDisplaySize(ICON, ICON).setScrollFactor(0);
        c.add(sprite);
        textX = ICON + GAP;
      }
      const txt = this.scene.add.text(textX, y + ICON / 2, row.text, withGameFont({ fontSize: '10px', color: '#bbddff' }))
        .setOrigin(0, 0.5).setScrollFactor(0);
      c.add(txt);
    });
    return rows.length * ROW_H;
  }

  private updateDetail(): void {
    const noSel = !this.selectedSpellId;
    this.detailIcon?.setVisible(!noSel);
    if (noSel) {
      [this.detailName, this.detailDesc, this.detailSummon, this.detailGrant].forEach(t => t?.setText(''));
      this.detailSpellInfo?.removeAll(true);
      return;
    }
    const spell = getSpellDefinition(this.selectedSpellId!);
    if (!spell) return;

    this.detailIcon?.setTexture(resolveSpellIcon(this.scene, this.selectedSpellId!)).setDisplaySize(DETAIL_ICON_SIZE, DETAIL_ICON_SIZE).setVisible(true);
    this.detailName?.setText(spell.nameEs);
    this.detailDesc?.setText(spell.descriptionEs);

    // Ficha iconográfica: una fila (icono + texto) por característica presente. Devuelve el
    // alto ocupado para reposicionar debajo el bloque de invocación / hechizo temporal.
    const infoH = this.rebuildSpellInfoRows(spell);
    const belowY = (this.detailSpellInfo?.y ?? this.detailSpellInfoY) + infoH + 6;
    this.detailSummon?.setY(belowY);

    // Ficha de la criatura invocada (si el hechizo invoca).
    const summon = getSummonDisplayForSpell(spell);
    if (summon) {
      const lines: string[] = [];
      const roleEs = summon.role === 'support' ? `Soporte${summon.specialtyEs ? ` (${summon.specialtyEs})` : ''}` : 'Combate';
      lines.push(`Invoca: ${summon.name}${summon.count > 1 ? ` x${summon.count}` : ''} — ${roleEs}`);
      lines.push(
        `❤ Vida ${summon.hp}   ⚔ Daño ${summon.minDamage}-${summon.maxDamage}   ⚡ Vel. ataque ${summon.attackSpeed.toFixed(2)}   ✦ Crítico ${Math.round(summon.critChance * 100)}%`
      );
      if (summon.manaReservePercent) {
        lines.push(`Reserva ${Math.round(summon.manaReservePercent * 100)}% maná` + (summon.respawnMs > 0 ? `   Resurrección ${(summon.respawnMs / 1000).toFixed(0)}s` : ''));
      } else if (summon.durationMs > 0) {
        lines.push(`Duración ${(summon.durationMs / 1000).toFixed(0)}s`);
      }
      this.detailSummon?.setText(lines.join('\n'));
    } else {
      this.detailSummon?.setText('');
    }
    // Hechizo temporal debajo del summon (o de la ficha si no hay summon).
    this.detailGrant?.setY((this.detailSummon?.y ?? belowY) + (this.detailSummon?.height ?? 0) + 4);

    // Hechizo temporal: marcar y decir qué item lo otorga.
    if (spell.grantOnly) {
      const granter = getItemGrantingSpell(spell.id);
      this.detailGrant?.setText(`⟳ Temporal — Otorgado por: ${granter?.name ?? 'un item equipado'}`);
    } else {
      this.detailGrant?.setText('');
    }

    this.assignBtn?.setVisible(true);
  }

  // === VISIBILITY & MOVE =====================================================
  private setVisible(v: boolean): void {
    const els: (Phaser.GameObjects.Components.Visible | null)[] = [
      this.bg, this.title, this.closeBtn, this.listBg, this.scrollUp, this.scrollDown,
      this.detailIcon, this.detailName, this.detailDesc, this.detailSpellInfo,
      this.detailSummon, this.detailGrant,
      this.assignBtn, this.instructionText, this.noSpellsText,
    ];
    for (const el of els) { el?.setVisible(v); if (el && 'setActive' in el) (el as any).setActive(v); }
    for (const row of this.listRows) {
      row.text.setVisible(v).setActive(v);
      row.icon.setVisible(v);
      row.hit.setVisible(v).setActive(v);
    }
    if (!v) this.cancelSpellDrag(); // no dejar un preview colgado al cerrar
  }

  private movePanel(nx: number, ny: number): void {
    const dx = nx - this.panelX, dy = ny - this.panelY;
    this.panelX = nx; this.panelY = ny;
    const m = (el: any) => { if (el) el.setPosition(el.x + dx, el.y + dy); };
    m(this.bg); m(this.title); m(this.closeBtn); m(this.listBg);
    m(this.scrollUp); m(this.scrollDown); m(this.detailIcon); m(this.detailName);
    m(this.detailDesc); m(this.detailSpellInfo); m(this.detailSummon); m(this.detailGrant);
    m(this.assignBtn); m(this.instructionText); m(this.noSpellsText);
    for (const r of this.listRows) { m(r.hit); m(r.icon); m(r.text); }
  }
}
